/**
 * Visual Debugger — domain types
 *
 * These mirror the entities in PRD §11 and are the contract between frontend and API.
 * Keep the shapes stable; add fields additively.
 */

// API error codes live in their own file so the union is easy to
// extend without scrolling past 600 lines of domain types.
export type { ApiErrorCode, ApiErrorBody } from './errors';
export { avatarGradient } from './avatar';

// Embedded-widget contract (config, theme, launcher, submissions). Its own
// module for the same reason, and strictly type-only — see the note at the
// top of widget.ts about keeping the extension bundle untouched.
//
// The import and the re-export are separate statements because `TicketSource`
// is also referenced by `TicketSummary` below; a bare `export type { … }`
// would re-export it without binding it in this module's scope. Both are
// type-only, so the index ⇄ widget cycle is erased at compile time.
import type { TicketSource } from './widget';

export type {
  TicketSource,
  WidgetCaptureTier,
  WidgetLauncherVariant,
  WidgetLauncherSide,
  WidgetLauncherVertical,
  WidgetLauncherStacking,
  WidgetLauncherClickAction,
  WidgetCaptureHotkey,
  WidgetLauncherIcon,
  WidgetLauncherConfig,
  WidgetTheme,
  WidgetLauncherStyle,
  WidgetPanelStyle,
  WidgetBorderStyle,
  WidgetShadow,
  WidgetFontWeight,
  WidgetFieldType,
  WidgetFieldDef,
  WidgetDialogVariant,
  WidgetDialogConfig,
  WidgetPrivacyConfig,
  WidgetPublicConfig,
  WidgetTicketTypeOption,
  WidgetRecordingOffer,
  WidgetNarration,
  WidgetOriginPolicy,
  WidgetSeenOrigin,
  WidgetVerification,
  WidgetOverviewItem,
  ProjectWidget,
  UpdateProjectWidgetBody,
  WidgetAutoStyleState,
  WidgetSubmissionResult,
} from './widget';

// ---- Constants ------------------------------------------------------------

/**
 * The literal title set on a ticket when capture upload creates it
 * without an explicit title. The AI title-generation worker checks for
 * this exact string before overwriting (so it never clobbers a user-
 * entered title), and frontends check for it to render the "AI is
 * writing the title" placeholder UI.
 *
 * If you change this string, every check across the codebase has to
 * change in lockstep. Don't.
 */
export const UNTITLED_CAPTURE_PLACEHOLDER = 'Untitled capture';

/**
 * The app's page for installing the extension, as a path on the app's own
 * origin. The sidebar's "Get the extension" opens it.
 */
export const EXTENSION_INSTALL_PATH = '/me/settings/extension';

/**
 * Toado's public page about the extension, which links to installing it.
 * The widget's "On the team? Get the extension" goes here: whoever clicks it,
 * on the team or not, lands on a page that says what Toado is rather than on
 * the app's sign-in (Jason, 18 Oct).
 */
export const EXTENSION_PAGE_URL = 'https://toado.dev/extension';

export { FROG_HOP_KEYFRAMES, FROG_HOP_LANDS_MS, FROG_HOP_MS, TOADO_LOGO_ASPECT, TOADO_LOGO_SVG } from './brand';

/**
 * How a ticket capture was initiated. Nullable database rows predate this
 * contract and must remain distinguishable from an explicit classic capture.
 */
export const CAPTURE_MODES = ['pin', 'edit', 'markup', 'classic', 'widget'] as const;
export type CaptureMode = typeof CAPTURE_MODES[number];

// ---- Identity & access ----------------------------------------------------

export type UserId = string;
export type CompanyId = string;
export type ProjectId = string;
export type ColumnId = string;
export type TicketId = string;
export type CommentId = string;

// 'viewer' is a read-only, free seat (VIEWER_SEATS_PRD.md). Doesn't
// count toward `seatsPurchased`; backend rejects mutations from viewers
// at the auth layer with VIEWER_READONLY.
export type Role = 'owner' | 'member' | 'viewer';

export interface User {
  id: UserId;
  email: string;
  name: string;
  avatarUrl?: string;
  /**
   * True ONLY for Toado staff (founder + future internal team). The
   * `/me` endpoint omits this field for non-staff users entirely — a
   * literal `false` would leak the field's existence and signal that
   * an admin surface exists, so the convention is "present means
   * true". Consumers should check `user.isStaff === true`.
   */
  isStaff?: true;
  /**
   * Which theme this person's Toado surfaces use.
   *
   * A user setting, not a per-device one, so the web app and the browser
   * extension's in-page panel agree: the extension cannot read the web app's
   * localStorage (different origin, different storage area), so the account
   * is the only place the two can meet.
   *
   * `'system'` is the default and is a preference, not a resolved value:
   * each surface answers it with its own `prefers-color-scheme` query.
   * Optional on the type because a `/me` response from before this field
   * existed does not carry it, and because the mock backend's fixtures do
   * not have to.
   */
  theme?: UserTheme;
  /**
   * The IANA time zone every time is shown in (TOADO-904); null until one is
   * saved, when the browser's is used. `auto` follows the browser and is
   * saved from it on load; `manual` was chosen in User settings.
   */
  timeZone?: string | null;
  timeZoneSource?: 'auto' | 'manual' | null;
}

/** The themes a Toado surface can be set to. Mirrors `users.theme`. */
export type UserTheme = 'system' | 'light' | 'dark';

export interface Company {
  id: CompanyId;
  name: string;
  role: Role;
  /**
   * Per-project access scope for the calling user's membership in this
   * company. Mirrors `Membership.accessMode`. Optional on the wire
   * because the dedicated `/companies/:id` endpoint may not include it;
   * `/me` always does. Defaults to `'all_projects'` when absent, which
   * matches the legacy behavior every existing membership ships with.
   */
  accessMode?: MembershipAccessMode;
  /** The workspace's colour, from the project palette (TOADO-906). */
  color?: string;
  /**
   * The workspace's logo, Toado's own stored copy of an owner's upload
   * (TOADO-906), through the authenticated storage route: members only, and
   * relative when the API has no public base address set, to be read from the
   * API. Null without one. Never an outside address, never a signed link.
   */
  logoUrl?: string | null;
  hasUploadedLogo?: boolean;
  /**
   * Features this workspace grants the caller (TOADO-493), resolved by the
   * server from its audience setting and the workspace's own switch. Only
   * `/me` sends it. A hint for the interface: the server asks again on every
   * write it gates.
   */
  features?: { recording: boolean };
  /**
   * Whether this company allows AI features (currently: ticket-title
   * auto-generation via Anthropic). Optional on the wire because the
   * /me/companies endpoint may not include it; the dedicated
   * /companies/:id endpoint always does.
   */
  aiEnabled?: boolean;
}

export interface Membership {
  userId: UserId;
  companyId: CompanyId;
  role: Role;
  /**
   * Per-project access scope, orthogonal to role. `all_projects`
   * (the default for every existing membership) means the member sees
   * every project in the company. `restricted` means they only see
   * projects explicitly granted in `member_project_access`.
   *
   * Owners are always exempt from restriction in practice — the
   * server's `canSeeProject` short-circuits on role === 'owner'.
   */
  accessMode: MembershipAccessMode;
  joinedAt: string;
  lastActiveAt?: string;
}

export type MembershipAccessMode = 'all_projects' | 'restricted';

/**
 * Shape of one entry in `GET /companies/:id/members` after the
 * per-project access feature. `projectIds` is the set of granted
 * projects when `accessMode === 'restricted'`; empty array for
 * all-projects members.
 */
export interface MemberListItem {
  user: { id: UserId; email: string; name: string; avatarUrl?: string };
  role: Role;
  accessMode: MembershipAccessMode;
  projectIds: ProjectId[];
}

/** Body of `PATCH /companies/:id/members/:userId/access`. */
export interface UpdateMemberAccessBody {
  accessMode: MembershipAccessMode;
  projectIds?: ProjectId[];
}

// ---- Projects & columns ---------------------------------------------------

export interface Project {
  id: ProjectId;
  companyId: CompanyId;
  name: string;
  description?: string;
  color: string; // CSS color for the dot
  urlPatterns: string[];
  /**
   * Every ticket not archived, drafts and done included.
   * @deprecated Read openCount, doneCount and archivedCount (TOADO-943).
   */
  ticketCount: number;
  /** Open tickets: not archived, not a draft, not in a done column. The sidebar's number (TOADO-943). */
  openCount?: number;
  /** In a done column, not archived, not a draft. */
  doneCount?: number;
  /** Archived, not deleted. */
  archivedCount?: number;
  /** Distinct people involved (ticket creators + assignees). */
  memberCount?: number;
  hasUnreadActivity: boolean;
  /** Per-project archival override. `null`/missing = inherit company default. */
  archivalDays?: number | null;
  /** TOADO-251: when true, anonymous widget submissions land in the column
   *  marked isIntake instead of the first column. Teammate captures are
   *  never routed to intake. */
  bugIntakeEnabled?: boolean;
  /**
   * TOADO-383: what a walkthrough recording of THIS project captures.
   * `null`/missing means the recorder's own setting decides, and that is
   * not a third mode - it is the absence of an opinion.
   */
  recordingMediaMode?: 'video' | 'audio' | null;
  /** TOADO-844: where the project's code lives, so a recorded fix's commit and pull request show as links. */
  repositoryUrl?: string | null;
  /** TOADO-998: how agents should work this project's tickets; null when the team has written none. */
  agentInstructions?: import('./agentInstructions').AgentInstructions | null;
  /**
   * The project's icon (16 Oct): a link to the site's own or an uploaded one,
   * null for the initials square. `iconChoice` 'initials' shows the initials
   * whatever is stored; `hasUploadedIcon` says an upload is there to remove.
   */
  iconUrl?: string | null;
  iconChoice?: 'auto' | 'initials';
  hasUploadedIcon?: boolean;
  /** When the site was last looked at for its icon; it moves when a look finishes. */
  iconCheckedAt?: string | null;
  /** The site a look reads for its icon: the one picked, else the first https URL pattern's origin; null when there is none. */
  iconSite?: string | null;
  /** The sites the URL patterns name, in order, each once: the choices for where the icon comes from (22 Oct). */
  iconSites?: string[];
  /** The site the owner picked for the icon, null for the first URL pattern's (22 Oct). */
  iconSitePicked?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** max(ticket.updatedAt) in this project — falls back to project.createdAt when empty. */
  lastActivityAt?: string;
  /**
   * When the current user last opened this project (server-synced across
   * devices). Undefined if they've never opened it. Drives sidebar MRU.
   */
  lastOpenedAt?: string;
}

export interface Column {
  id: ColumnId;
  projectId: ProjectId;
  name: string;
  order: number;
  color?: string;
  /** Optional archival policy override for this column. */
  archivalDays?: number | null;
  /** TOADO-251: the bug-intake marker. When the project's bugIntakeEnabled
   *  toggle is on, anonymous widget submissions route to this column.
   *  Routing follows this flag, never the name or position. */
  isIntake?: boolean;
  /** TOADO-723: its tickets wait on whoever reported them; the Dashboard lists theirs under Waiting on your reply. */
  waitingOnReporter?: boolean;
  /** Where tickets finish (the done column). Kept by a columns save that does not send it (TOADO-970). */
  isTerminal?: boolean;
  /** Its dot's colour, worked out on the web from its place on the board (`withColumnTones`), never stored. */
  tone?: ColumnTone;
}

/** The colour of a column's dot, wherever one is drawn. */
export type ColumnTone = 'neutral' | 'blue' | 'amber' | 'violet' | 'green';

const BETWEEN_TONES: readonly ColumnTone[] = ['blue', 'amber', 'violet'];

/**
 * A column's colour by its role on its board (Jason, 19 Oct): the first green,
 * the last grey (where work ends up, Done on a new project), and those
 * between blue, amber, violet, then round again. By role rather than by
 * position alone, so the first and the last never share a colour however
 * many columns a project has. `index` is its place among `count` columns.
 */
export function columnTone(index: number, count: number): ColumnTone {
  if (index <= 0 || count <= 1) return 'green';
  if (index === count - 1) return 'neutral';
  return BETWEEN_TONES[(index - 1) % BETWEEN_TONES.length]!;
}

/** One project's columns, each given its `tone` by its place in board order. */
export function withColumnTones<C extends Pick<Column, 'id' | 'order'>>(columns: readonly C[]): Array<C & { tone: ColumnTone }> {
  const ranked = [...columns].sort((a, b) => a.order - b.order || compareByCodePoint(a.id, b.id));
  const place = new Map(ranked.map((c, i) => [c.id, i]));
  return columns.map((c) => ({ ...c, tone: columnTone(place.get(c.id)!, columns.length) }));
}

// ---- Tickets --------------------------------------------------------------

export type TicketHealth = 'complete' | 'partial' | 'failed';

export interface TicketHealthDetail {
  status: TicketHealth;
  /** Human-readable explanation surfaced in the banner/tooltip. */
  message?: string;
  /** Which categories were captured successfully. */
  captured: {
    screenshot: boolean;
    console: boolean;
    network: boolean;
    errors: boolean;
    performance: boolean;
    url: boolean;
  };
}

/**
 * What fixed a ticket, as whoever fixed it recorded it (TOADO-844, MCP
 * `record_fix`). At least one of `commit`, `pullRequestUrl` and `branch`.
 */
export interface TicketFix {
  id: string;
  ticketId: string;
  /** A commit hash, lower case. */
  commit: string | null;
  /** An http or https address. */
  pullRequestUrl: string | null;
  branch: string | null;
  /** One line saying what changed, in the fixer's words. */
  summary: string | null;
  /** The person, or the person whose token the agent used. */
  recordedBy: string;
  /** The token's label when an agent recorded it, as a comment's `authorTokenLabel`. */
  viaAgent: string | null;
  createdAt: string;
}

/**
 * What kind of thing a ticket is (TOADO-407).
 *
 * The identity is company-level and the name may be overridden per project, so
 * both travel: an agent and a filter want `id`, a person reading a card wants
 * `name`, and the panel wants `glyph`. Sending only the id would make every
 * card render depend on a second lookup.
 */
export interface TicketTypeRef {
  id: string;
  /** The name in effect here, which may be a project's local override. */
  name: string;
  /** The company-level name, present only when a project has renamed it. */
  companyName?: string;
  /** A short lowercase token naming a marker. See TICKET_TYPE_GLYPH_PATTERN. */
  glyph: string;
}

/** Which built-in type a row is: its default glyph, and the recorder's key for it. */
export type BuiltinTicketTypeKey = 'bug' | 'improvement' | 'feature';

/** A company's full type, as the settings surfaces edit it. */
export interface TicketType {
  id: string;
  name: string;
  glyph: string;
  order: number;
  defaultForNewProjects: boolean;
  /**
   * Set on Bug, Improvement and New feature (TOADO-578), null on a type a
   * workspace added. A built-in type can be turned off for a project, and the
   * workspace can choose whether new projects get it, but it cannot be
   * deleted, renamed or given another icon; the API refuses all three.
   * Optional because the defaults below describe types not yet created.
   */
  builtin?: BuiltinTicketTypeKey | null;
}

/** One type as it applies to a project, after the project's delta. */
export interface ProjectTicketType extends TicketType {
  /** The company-level name, when this project has renamed it. */
  companyName?: string;
  /** Hidden types are not offered for new tickets here. Never retroactive. */
  hidden: boolean;
}

/**
 * What a fresh company starts with.
 *
 * Here rather than in the API so the seed, the settings page's empty state and
 * the classifier's fallback cannot drift into three different opinions about
 * what the default vocabulary is.
 */
export const DEFAULT_TICKET_TYPES: ReadonlyArray<Omit<TicketType, 'id'>> = [
  { name: 'Bug', glyph: 'bug', order: 0, defaultForNewProjects: true },
  { name: 'Improvement', glyph: 'improvement', order: 1, defaultForNewProjects: true },
  { name: 'New feature', glyph: 'feature', order: 2, defaultForNewProjects: true },
];

/**
 * Where a ticket stands with Jira (TOADO-708). A summary leaves it out for a
 * ticket never sent. `error` is set only when the last send failed.
 */
export interface TicketJira {
  state: 'pending' | 'exported' | 'failed';
  issueKey: string | null;
  issueUrl: string | null;
  error: string | null;
  /**
   * The ticket's version when this was read. Every Jira state change moves it,
   * so of two announcements heard out of order, the one with the higher
   * revision is the later.
   */
  revision: number;
}

/** An agent at work on a ticket (t-gdi2Cd). See `TicketSummary.agentWorking`. */
export interface AgentWorking {
  /** The AI tool by its own name ("Claude Code"), or "AI" (`workingAgentName`). */
  client: string;
  /** The column the agent moved the ticket into. */
  columnId: ColumnId;
  /** The agent's latest act on the ticket, ISO. */
  lastActiveAt: string;
}

/** Between the device and the client in a label the OAuth connection made: a middle dot, spaced. */
const AGENT_LABEL_SEPARATOR = ` ${String.fromCharCode(0xb7)} `;

/**
 * The name an agent goes by (TOADO-724): a connection's label reads "device,
 * middle dot, client", and the client is the agent; a label a person typed is
 * theirs, shown as typed. Shared so the board names an agent from a comment's
 * token label exactly as the server names it from the token.
 */
export function agentName(label: string | null | undefined): string {
  const named = (label ?? '').trim();
  if (!named) return 'An agent';
  const parts = named.split(AGENT_LABEL_SEPARATOR);
  return parts[parts.length - 1]!.trim() || named;
}

/**
 * AI tools a connection's name may be, matched as whole words. A tool names
 * itself when it connects (its OAuth `client_name`), and a person names a
 * token they make, so a label alone cannot say which it is.
 */
const KNOWN_AI_TOOLS = /\b(claude|codex|chatgpt|openai|gpt|gemini|copilot|cursor|windsurf|cline|devin|aider|goose|grok|mistral|perplexity|kiro|junie|jules)\b/i;

/**
 * Who a card says is working on a ticket (t-gdi2Cd): the AI tool's own name
 * when the connection carries one we recognise ("Claude Code", "Codex"), and
 * "AI" otherwise, so a generic "MCP client" or a token a person called
 * "work laptop" never reads as the name of whatever is doing the work
 * (Jason, 18 Oct). The name is the tool's own claim; nothing verifies it.
 */
export function workingAgentName(label: string | null | undefined): string {
  const name = agentName(label);
  return KNOWN_AI_TOOLS.test(name) ? name : UNNAMED_AGENT;
}

/** How each known tool is written on a chip (TOADO-951): the maker's own spelling. */
const AI_TOOL_NAMES: Record<string, string> = {
  claude: 'Claude', codex: 'Codex', chatgpt: 'ChatGPT', openai: 'OpenAI', gpt: 'ChatGPT', gemini: 'Gemini',
  copilot: 'Copilot', cursor: 'Cursor', windsurf: 'Windsurf', cline: 'Cline', devin: 'Devin', aider: 'Aider',
  goose: 'Goose', grok: 'Grok', mistral: 'Mistral', perplexity: 'Perplexity', kiro: 'Kiro', junie: 'Junie', jules: 'Jules',
};

/**
 * The AI tool a connection's name says it is ("Claude" for "Claude Code"), or
 * null for a name that says none, such as a token a person typed (TOADO-951).
 * The same list `workingAgentName` reads; the name is the tool's own claim.
 */
export function knownAiTool(label: string | null | undefined): string | null {
  const found = KNOWN_AI_TOOLS.exec(label ?? '');
  return found ? AI_TOOL_NAMES[found[1]!.toLowerCase()] ?? null : null;
}

/** What `workingAgentName` calls an agent it cannot name. */
export const UNNAMED_AGENT = 'AI';

/** How long an agent may go without touching a ticket and still count as working it. */
export const AGENT_WORKING_IDLE_MS = 30 * 60 * 1000;

/**
 * The agent at work on this ticket now, or null: the one the summary names,
 * while the ticket is still in the column it moved it into and it has not
 * gone quiet. The server applies the same rule when it lists the board; this
 * is the board's half, for a card whose summary is older than the clock.
 */
export function agentWorkingNow(
  ticket: Pick<TicketSummary, 'columnId' | 'agentWorking'>,
  now: number,
): AgentWorking | null {
  const agent = ticket.agentWorking;
  if (!agent || agent.columnId !== ticket.columnId) return null;
  const active = Date.parse(agent.lastActiveAt);
  return Number.isFinite(active) && now - active < AGENT_WORKING_IDLE_MS ? agent : null;
}

/**
 * What a name from a walkthrough's narration is, as a note offers it (TOADO-471).
 * Clear unique requests can be assigned automatically; unresolved claims use
 * these existing suggestion and refusal reasons.
 *
 * - `named`: a plain sentence asked for exactly one person on the project.
 * - `hedged`: the sentence named them alongside a "not", "but", "rather" or
 *   the like, so it may not be asking them.
 * - `ambiguous`: more than one person on the project has that name.
 * - `close_spelling`: nobody has it exactly, but these people have a name one
 *   misheard letter away: "John" is not Joan.
 * - `no_match`: nobody on the project has it.
 * - `removed_by_person`: a person picked them from a note and later took them off.
 * - `no_project_access`: the person named cannot see this project.
 */
export type NarrationAssignmentReason =
  | 'named'
  | 'hedged'
  | 'ambiguous'
  | 'close_spelling'
  | 'no_match'
  | 'removed_by_person'
  | 'no_project_access';

export interface NarrationAssignmentPerson {
  id: UserId;
  name: string;
  avatarUrl?: string;
}

/**
 * One name the narration produced for a ticket, as the ticket shows it.
 *
 * Only what a person can still act on is returned: a suggestion nobody has
 * resolved, and a match a guard stopped.
 */
export interface NarrationAssignmentNote {
  id: string;
  outcome: 'suggested' | 'not_applied';
  reason: NarrationAssignmentReason | null;
  /** As it was said. Empty when the person recording said they would take it. */
  heardName: string;
  isSelf: boolean;
  /**
   * The words it came from. Null when they were said while describing a ticket
   * that has since moved to another project or been deleted: its narration is
   * not repeated to people who may not be able to see it.
   */
  sentence: string | null;
  /** Who a guard stopped, for a note that is not a suggestion. */
  person: NarrationAssignmentPerson | null;
  /** Who a person may pick from here. Empty when there is nobody to pick. */
  candidates: NarrationAssignmentPerson[];
}

export interface NarrationAssignmentsResponse {
  notes: NarrationAssignmentNote[];
  /** Whether the caller may assign or dismiss. False for a viewer. */
  canChange: boolean;
}

export interface TicketSummary {
  id: TicketId;
  projectId: ProjectId;
  columnId: ColumnId;
  title: string;
  /**
   * Which kind of thing this is, or absent (TOADO-407).
   *
   * Absent is a legal state, not a gap: tickets that predate the field, ones an
   * agent created over MCP without naming a type, and widget reports from
   * outside the workspace all have none.
   */
  type?: TicketTypeRef;
  /**
   * True when the title is a placeholder ("Untitled capture") and the
   * worker is queued to auto-generate one from the description. UIs
   * should render a "Generating title…" state when this is true.
   */
  titlePending?: boolean;
  createdBy: UserId;
  createdAt: string;
  sourceUrl: string;
  thumbnailUrl?: string;
  /** The ticket has a walkthrough video to watch, ready or still being cut (22 Oct): the card shows a video mark. */
  hasVideo?: boolean;
  /** Something for the Session window: a trail of the visit, or console, network or errors (TOADO-945). */
  hasSession?: boolean;
  commentCount: number;
  assignees: UserId[];
  hasAiActivity: boolean;
  /**
   * An agent at work on the ticket, for the board card's shimmer (t-gdi2Cd):
   * it made the last move into `columnId` and has touched the ticket since
   * `lastActiveAt`. Absent when no agent is. A card shows it only while the
   * ticket is still in that column and the agent has not gone quiet for
   * `AGENT_WORKING_IDLE_MS`, so a board that applies live events to its
   * cached cards stops the shimmer without a refetch.
   */
  agentWorking?: AgentWorking;
  resolved: boolean;
  health: TicketHealth;
  archivedAt?: string;
  /**
   * When an archived ticket is deleted, for one removed from a page with the
   * extension (TOADO-442). Absent for any other ticket: they stay archived.
   */
  purgeAt?: string;
  /**
   * Short preview of the description (first ~240 chars). Returned by
   * the summary endpoints so list/table views can show a description
   * snippet or tooltip without fetching the full ticket per row. The
   * full text still lives on the Ticket payload from /tickets/:id.
   */
  descriptionPreview?: string;
  /** Where the ticket stands with Jira; absent when it was never sent (TOADO-708). */
  jira?: TicketJira;
  /** Last-modified timestamp; included by the API serializer. */
  updatedAt?: string;
  /**
   * Monotonic change counter; included by the API serializer. Bumps on
   * every response-visible write — user edits (mutateTicket), comment
   * create/edit/delete, annotation saves, archival, AND system writes
   * like the AI-title worker and column-deletion reassignment. Used as
   * the annotated-raster cache-bust key and as the ETag validator on
   * GET /tickets/:id (TOADO-124). NOT an optimistic-concurrency token:
   * no client sends an expected version and no endpoint compares one.
   * Don't build OCC on this field without first narrowing when it
   * bumps — system writes would make user-facing conflicts spurious.
   */
  version?: number;
  /**
   * User ids @-mentioned in the ticket description. Populated by the
   * mention parser on every description write. Comment-level mentions
   * live on the Comment payload, not here.
   */
  mentions?: UserId[];
  /**
   * Git branch this ticket is being worked on. Null until someone
   * starts work — set via PATCH /tickets/:id or the MCP
   * `start_work_on_ticket` tool. Free-form text; no validation that
   * the branch actually exists on a remote.
   *
   * Null vs undefined: the server's `toSummary` emits the DB value
   * directly (string for set, null for cleared). Without preserving
   * the explicit null, the live-event update path (see board's
   * ProjectViewsPage subscriber) couldn't propagate a "user cleared
   * the branch name" change to other tabs — `JSON.stringify` strips
   * undefined values, and the receiving client's `{...t, ...e.fields}`
   * merge wouldn't touch the cached branchName.
   */
  branchName?: string | null;
  /**
   * True while the creator is still writing the first version of the
   * ticket. Capture-flow inserts default this to true; the explicit
   * "Save & return" / Cmd+Enter action on the capture detail tab flips
   * it false. List endpoints (board, archive, MCP) filter drafts out;
   * only the dedicated Drafts page returns them.
   */
  isDraft?: boolean;
  /**
   * The sample ticket a project made in the app starts with (TOADO-988):
   * badged on its card, opened by the board tour, left out of the monthly
   * count. Absent on every other ticket.
   */
  isSample?: true;
  /**
   * How this ticket entered the workspace. Absent on responses from an
   * older api build; treat `undefined` as `'extension'` for display, since
   * every pre-widget capture came from there.
   */
  source?: TicketSource;
  /**
   * Capture interaction that created the ticket. Null is historical and must
   * remain distinct from classic; undefined means an older API omitted it.
   */
  captureMode?: CaptureMode | null;
  /**
   * Self-reported contact address on an anonymous widget or public-link
   * submission. **Claimed, never verified** — no email round-trip happens,
   * so it must be rendered as a claim ("reported by …") and never used as
   * an identity for access decisions.
   */
  reporterEmail?: string | null;
  /** Self-reported display name; same "claimed, not verified" caveat. */
  reporterName?: string | null;
}

/**
 * Which screen of its page a pin was placed on (TOADO-702), for a single-page
 * app where several screens share one address. Words read from somebody else's
 * page, already redacted and normalised (lower case, no digits): rendered as
 * text and never as markup, and never mixed into the description, which an
 * agent reads as instructions. `source` says so in the field itself.
 */
export interface TicketScreen {
  source: 'page_text';
  label: string;
  site?: string;
  container?: string;
  nav?: string[];
  heading?: string;
  title?: string;
  global?: true;
}

/**
 * What people decided about where a pin shows (TOADO-698): "Show on every
 * screen" either way, and the screens it is hidden on. Its shape is
 * `ScreenRule` in `@vd/element-locator/screen`, declared here for readers of
 * the ticket; a reader that acts on it parses it with that package's parser.
 */
export interface TicketScreenRule {
  v: 1;
  every?: boolean;
  hidden?: Array<{ kind: string; value: string }>;
}

export interface Ticket extends TicketSummary {
  description: string;
  shareableLinkSlug: string;
  healthDetail: TicketHealthDetail;
  /** Where a pin was placed, when it was placed on a screen Toado could name. */
  screen?: TicketScreen;
  /** Where a pin shows, when somebody has changed it; absent when it has its defaults. */
  screenRule?: TicketScreenRule;
  attachments: Attachment[];
  annotations: AnnotationRef[];
  capture: CaptureRef;
  /** Additional ready snapshots retained from a walkthrough segment. */
  additionalCaptures?: AdditionalCaptureRef[];
  // DevTools data inlined per BACKEND_PRD §2.4 (v1). Undefined on tickets
  // without an associated capture (text-only, seeded, etc.).
  console?: ConsoleMessage[];
  network?: NetworkRequest[];
  errors?: CapturedError[];
  performance?: PerformanceSnapshot;
}

export interface CaptureRef {
  captureId?: string;
  screenshotUrl: string;
  screenshotAnnotatedUrl?: string;
  clipUrl?: string;
  /**
   * What the walkthrough recorded (TOADO-380).
   *
   * Sent even when there is no clip to play, so a reader deciding what to
   * render does not have to infer it from a URL that is absent for three
   * different reasons - never recorded, still processing, or pruned. Absent
   * on an ordinary capture, which has no walkthrough behind it at all.
   */
  clipKind?: 'video' | 'audio';
  /**
   * The clip was pruned rather than never recorded (TOADO-367).
   *
   * Distinct from `clipUrl` being absent, which is every ordinary capture
   * ticket. Without the distinction a walkthrough whose video aged out looks
   * identical to a ticket that never had one, and the person is left
   * wondering whether it failed.
   */
  clipExpired?: boolean;
  /**
   * The clip is still being cut (TOADO-900): the moment was filed as a ticket
   * during the recording, before its video existed. Cleared when it lands.
   */
  clipProcessing?: boolean;
  recording?: { sessionId: string; segmentId: string };
  consoleUrl?: string;
  networkUrl?: string;
  errorsUrl?: string;
  performanceUrl?: string;
  /**
   * The browser it was taken in, as the server keeps it: for a pin, the
   * window's size and the frame its element was in when there was one
   * (TOADO-828); the user agent and the page's settings (TOADO-907).
   */
  browser?: CaptureBrowser;
  /**
   * When the widget that filed it started hearing the page's console and
   * errors (epoch ms, TOADO-910), so an empty console can say what it is empty
   * since. Absent on extension captures and on a Minimal widget's.
   */
  collectingSince?: number;
  /**
   * The visit a widget report came from (TOADO-911): its pages, the last
   * things the person did, their setup, what the site said of itself and the
   * tab's earlier reports. Widget reports on Standard and Deep only.
   */
  session?: import('./sessionTrail').SessionTrail;
}

/** What a capture records of the browser it was taken in (TOADO-907). */
export interface CaptureBrowser {
  userAgent?: string;
  /** `request` when the page sent none and the upload's own User-Agent header was kept. */
  uaSource?: 'page' | 'request';
  viewport?: { width: number; height: number };
  pinFrame?: { width: number; height: number };
  devicePixelRatio?: number;
  /**
   * What a request's `startedAt` counts from (epoch ms), so the Session
   * window can place it in time: a widget report's page time origin
   * (`performance.timeOrigin`, TOADO-911), or the start of an extension
   * capture's earliest request (TOADO-918).
   */
  timeOrigin?: number;
  timezone?: string;
  colorScheme?: 'light' | 'dark' | 'no-preference';
  reducedMotion?: boolean;
  /**
   * A Chromium browser's client hints (TOADO-907), checked by
   * `cleanClientHints`: the real OS version and full browser version the user
   * agent freezes, the CPU architecture, and a phone's model.
   */
  platformVersion?: string;
  fullVersion?: string;
  architecture?: string;
  model?: string;
  /** Read from the user agent by the API as it answers (parseUserAgent); not stored. */
  browserName?: string;
  browserVersion?: string;
  osName?: string;
  osVersion?: string;
}

export interface AdditionalCaptureRef {
  id: string;
  snapshotId: string;
  screenshotUrl: string;
  thumbnailUrl?: string;
  capturedAtMs: number;
  trigger: 'start' | 'finish' | 'manual';
  sourceUrl: string;
}

export interface Attachment {
  id: string;
  ticketId: TicketId;
  uploadedBy: UserId;
  /**
   * The carousel image id annotation shapes are tagged by
   * (Shape.imageId). Server falls back to the row id for rows without
   * a client-supplied id, so consumers can always key on it.
   */
  imageId: string;
  /** Display name: the uploaded file name or "Screen HH:MM:SS". */
  label: string;
  /** Resolved fetchable URL (presigned or authenticated proxy path). */
  imageUrl: string;
  sizeBytes: number;
  createdAt: string;
}

export interface AnnotationRef {
  id: string;
  /** Monotonic row version; seeds the live-event ordering guard. */
  version?: number;
  targetId: TicketId | string; // attachment id or ticket id
  createdBy: UserId;
  updatedAt: string;
  /** Serialized vector strokes (tldraw snapshot). */
  snapshot: unknown;
}

// ---- Comments -------------------------------------------------------------

export type CommentAuthorType = 'user' | 'mcp_client';

/** The longest comment body the API takes, in characters; the extension holds its writes to it too. */
export const COMMENT_MAX_CHARS = 20 * 1024;

export interface Comment {
  id: CommentId;
  ticketId: TicketId;
  authorType: CommentAuthorType;
  authorId: string;
  authorDisplayName: string;
  /** For MCP authors, the token label (e.g., "Jason's laptop"). */
  authorTokenLabel?: string;
  body: string;
  createdAt: string;
  updatedAt?: string;
  /** User ids @-mentioned in the comment body. */
  mentions?: UserId[];
}

// ---- Assignments ----------------------------------------------------------

export interface Assignment {
  ticketId: TicketId;
  userId: UserId;
  assignedBy: UserId;
  assignedAt: string;
}

// ---- Captured DevTools data ----------------------------------------------

/** `debug` since TOADO-910, heard by the widget and, from 0.2.0, the extension. */
export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface ConsoleMessage {
  timestamp: number;
  level: ConsoleLevel;
  message: string;
  source?: string;
  stack?: string;
  /** URL of the frame this entry was captured in. Stamped at buffer-push
   * time so it survives even if the frame later navigates or is removed.
   * Optional for backward compat — entries from older v0.1.3 captures
   * (top-frame-only) have no frameUrl. */
  frameUrl?: string;
}

export interface NetworkRequest {
  id: string;
  method: string;
  url: string;
  status: number;
  durationMs: number;
  requestSize?: number;
  responseSize?: number;
  requestHeaders: Record<string, string>;
  responseHeaders: Record<string, string>;
  requestBody?: string;
  responseBody?: string;
  bodyTruncated?: boolean;
  streaming?: boolean;
  /** See ConsoleMessage.frameUrl. */
  frameUrl?: string;
  /** When it started, in ms from the capture's `browser.timeOrigin` (TOADO-918). */
  startedAt?: number;
  /**
   * When it started, epoch ms, as the extension records it; turned into
   * `startedAt` before the capture leaves the browser, and never kept.
   */
  began?: number;
}

export interface CapturedError {
  timestamp: number;
  message: string;
  stack?: string;
  count: number; // grouped identical errors
  source?: string;
  /**
   * Origin classification. The browser fires `window.error` for ANY error
   * on the page, including ones thrown from other extensions injecting
   * into the document. Those errors are noise from the user's POV and
   * shouldn't trigger triage on their behalf. Captured here so the UI
   * + MCP can hide / down-rank them while still preserving the data.
   *
   *   'app'        — error originates from the captured page's own JS
   *   'extension'  — error originates from another browser extension
   *                  (chrome-extension:// or moz-extension:// in the stack)
   *   'unknown'    — no source info; treat as 'app' for triage purposes
   */
  origin?: 'app' | 'extension' | 'unknown';
  /** See ConsoleMessage.frameUrl. */
  frameUrl?: string;
}

/**
 * What a performance reading may be, from the extension's worker that checks
 * the page's answer to every reader of a stored one (the ticket's Performance
 * tab, the MCP summary), so the capture, a person and an agent agree (review,
 * 23 Oct). A timing is at most a day; a layout-shift score at most 100; the
 * newest 200 long tasks are kept.
 */
export const PERFORMANCE_BOUNDS = { maxTimingMs: 24 * 60 * 60 * 1000, maxCls: 100, maxLongTasks: 200 } as const;

/**
 * A page's performance at capture time. Times are in milliseconds from the
 * start of the navigation, as the browser reports them; CLS is a unitless
 * layout-shift score. A reading the browser did not give is absent.
 */

export interface PerformanceSnapshot {
  lcp?: number;
  fcp?: number;
  cls?: number;
  inp?: number;
  /** Time to the document's first byte, from the navigation's start (Jason, 23 Oct). */
  ttfb?: number;
  /** When the document's DOMContentLoaded handlers had all run. */
  domContentLoaded?: number;
  longTasks: { startTime: number; durationMs: number }[];
  /** How long the page took to finish loading, once it had. */
  navigation?: { loadEvent: number };
}

// ---- MCP tokens & invites -------------------------------------------------

export interface ApiToken {
  id: string;
  userId: UserId;
  label: string;
  scope: {
    companies?: CompanyId[];
    projects?: ProjectId[];
    readOnly: boolean;
    /** MCP scopes (TOADO-470). Absent on tokens saved before the modal chose them. */
    mcp?: string[];
  };
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
  /** What made it (TOADO-770): a token you created, the browser extension's connection, or a widget team sign-in. */
  kind?: 'mcp' | 'extension' | 'widget_frame';
  /** When it stops working, if it does. */
  expiresAt?: string;
}

export interface Invite {
  id: string;
  companyId: CompanyId;
  email: string;
  role: Role;
  invitedBy: UserId;
  token: string;
  expiresAt: string;
  acceptedAt?: string;
  /** TOADO-243: null = staged (created by staff, never emailed, inert
   *  until sent). Absent on rows from servers predating the field. */
  sentAt?: string | null;
  // Invite-time project access policy. Replayed onto the consuming
  // user's membership at accept time so a restricted invitee never
  // sees projects they weren't granted, even momentarily.
  accessMode: 'all_projects' | 'restricted';
  projectIds: string[];
}

// ---- Live update events ---------------------------------------------------

/**
 * Shape every LiveEvent shares — the ticketId scope. Consumers narrow on
 * this rather than `'ticketId' in e` sniffs (every variant has it
 * today, but explicit > implicit) and can use `isLiveTicketEvent(e, id)`
 * to filter to events for a specific ticket.
 */
export interface TicketScopedLiveEvent {
  ticketId: TicketId;
}

/** The most tickets one `POST /tickets/bulk-move` takes. */
export const BULK_MOVE_MAX_TICKETS = 100;

/**
 * One ticket's answer in `POST /tickets/bulk-move`, in the order the tickets
 * were asked for: moved, or not with the status and code a request to
 * `/tickets/:id/move` for it alone would have answered. A 500 is a move
 * that failed partway and may have been stored; a 409 `not_attempted` is one
 * never tried because an earlier ticket's failed that way.
 */
export type BulkMoveResult =
  | { ticketId: string; moved: true }
  | { ticketId: string; moved: false; status: number; code: string; message: string };

/**
 * What a `ticket.updated` says changed, merged by subscribers into their
 * cached summaries.
 *
 * `type` is the ticket's type as this project names it, or `null` when it was
 * cleared: a summary leaves the field out for an untyped ticket, and an absent
 * field on the wire would read as "unchanged". A type change also changes which
 * type-filtered lists a ticket belongs in, so those are refetched rather than
 * patched.
 */
export type TicketUpdatedFields =
  Partial<Pick<Ticket, 'title' | 'description' | 'titlePending' | 'resolved' | 'branchName' | 'mentions' | 'thumbnailUrl' | 'health'>>
  & {
    type?: TicketTypeRef | null;
    /** Where a pin shows, as stored after a change (TOADO-698), or null when it has gone back to its defaults. */
    screenRule?: TicketScreenRule | null;
    /** Where the ticket stands with Jira after a send changed it (TOADO-708); null if it has none. */
    jira?: TicketJira | null;
    /**
     * The ticket's version as that Jira state was read, sent with every Jira
     * announcement, a cleared one included: a view never lets an older word,
     * a link or its removal, over a newer one. The same number as `jira.revision`
     * when there is a state: both are the one row's version, read once; this
     * field exists because a removal has no state to carry it.
     */
    jiraRevision?: number;
  };

export type LiveEvent =
  | (TicketScopedLiveEvent & { type: 'comment.created'; comment: Comment })
  // Carries projectId (unlike most ticket.* events) so any view showing a
  // project-scoped list — board, all-tickets, inbox-by-project — can
  // invalidate just its own list without a per-event DB lookup.
  | (TicketScopedLiveEvent & { type: 'ticket.created'; projectId: ProjectId })
  // `agent` names the agent when an MCP agent made the move (t-gdi2Cd), so an
  // open board starts the card's shimmer without a refetch; absent for anyone else.
  | (TicketScopedLiveEvent & { type: 'ticket.moved'; fromColumnId: ColumnId; toColumnId: ColumnId; by: string; agent?: string })
  | (TicketScopedLiveEvent & { type: 'ticket.assigned'; assignees: UserId[]; by: string })
  // `fields` covers what a PATCH /tickets/:id can change in one go.
  // Subscribers merge any present fields into their cached summary
  // (see board's live-event handler in ProjectViewsPage). Adding a
  // field here requires the server's broadcastFields to include it
  // too — otherwise multi-user updates on that field stay invisible
  // until next list refetch.
  | (TicketScopedLiveEvent & { type: 'ticket.updated'; fields: TicketUpdatedFields })
  | (TicketScopedLiveEvent & { type: 'ticket.archived' })
  | (TicketScopedLiveEvent & { type: 'ticket.restored' })
  | (TicketScopedLiveEvent & { type: 'ticket.deleted' })
  | (TicketScopedLiveEvent & { type: 'capture.ready'; tab: 'console' | 'network' | 'errors' | 'performance' })
  // `version` is the annotations row's monotonic version after the
  // write that produced this event. Clients drop events older than the
  // newest version they have applied, so a stale save's event arriving
  // after a delete's pruned event cannot briefly resurrect shapes.
  // Optional for wire compat with events emitted by older api builds.
  | (TicketScopedLiveEvent & { type: 'annotation.updated'; targetId: string; snapshot: unknown; by: UserId; version?: number })
  // Member access-mode or project-grant set changed. Broadcast on the
  // company channel; the affected user's tabs invalidate `/me` +
  // project queries on receipt. Payload deliberately carries only the
  // userId (not projectIds) so the broadcast can't leak which projects
  // were granted/revoked to other tabs subscribed to the same channel.
  | { type: 'member.access_changed'; companyId: CompanyId; userId: UserId };

/**
 * Type guard for "this LiveEvent is for ticket X." Replaces the
 * `'ticketId' in e && e.ticketId === id` pattern at consumer sites
 * (e.g., `apps/web/src/features/ticket/TicketDetail.tsx`'s subscribe
 * callback) with a single named check that's obvious in a code review.
 */
export function isLiveTicketEvent(e: LiveEvent, ticketId: TicketId): boolean {
  return 'ticketId' in e && e.ticketId === ticketId;
}

/**
 * Compute the `descriptionPreview` field used on `TicketSummary` — the
 * first 240 chars of the description with internal whitespace collapsed
 * to single spaces. Big enough for a tooltip/snippet, small enough not
 * to bloat list responses.
 *
 * Shared so the server (api's `toSummary` serializer) and the client
 * (board live-event handler when description changes) can never drift.
 * Both call sites would otherwise inline the same regex + slice +
 * trim chain, and any tweak would have to be made twice.
 */
export function buildDescriptionPreview(description: string | null | undefined): string | undefined {
  if (!description) return undefined;
  const trimmed = description.replace(/\s+/g, ' ').trim().slice(0, 240);
  return trimmed || undefined;
}

// ---- View preferences -----------------------------------------------------

export type ViewMode = 'board' | 'cards' | 'single' | 'table';

export interface ProjectViewPrefs {
  viewMode: ViewMode;
  thumbnails: boolean;
  /**
   * `user-sorted` preserves the drag-and-drop order on the kanban board
   * (tickets stay where the user dropped them within a column). The
   * other modes apply a per-column sort that overrides drag order on
   * every render — drag-between-columns still works (changes columnId)
   * but drag-within-column snaps back. `user-sorted` is the default;
   * users opt in to one of the others when they want explicit ordering.
   */
  sort: 'user-sorted' | 'newest' | 'oldest' | 'last-activity' | 'most-comments';
  sidebarCollapsed: boolean;
  quickFilters: {
    hasAiActivity: boolean;
    capturePartial: boolean;
    unresolved: boolean;
    mentionsMe: boolean;
    archived: boolean;
    /**
     * Only tickets reported by someone outside the workspace — the
     * embedded widget or the public report link.
     *
     * A distinct triage queue in practice: these come from people who
     * usually cannot be asked a follow-up question, so they need
     * different handling from a colleague's capture. Optional so prefs
     * saved before this filter existed still parse.
     */
    externalReports?: boolean;
  };
  assigneeIds?: string[];
  authorIds?: string[];
  /**
   * Column ids the user wants to limit the view to. Empty / unset =
   * no column filter (show all). Persisted in prefs so the filter
   * survives refresh + view-mode switches just like assignee/author
   * filters do.
   */
  columnIds?: string[];
  /**
   * Created-at range filter, both bounds optional. Values are the
   * raw strings from <input type="datetime-local"> ("YYYY-MM-DDTHH:mm")
   * — the browser interprets them in the user's local timezone, which
   * is what users expect when filtering by "tickets I made yesterday."
   * Filter logic compares via new Date(value).getTime() against
   * ticket.createdAt.
   */
  createdFrom?: string;
  createdTo?: string;
  /**
   * Ticket type ids the view is limited to (TOADO-411).
   *
   * The literal `'none'` is a member of this set and means "tickets with no
   * type". It is not a sentinel bolted on afterwards: on the day this ships most
   * of a workspace is untyped, and a filter that could not find those would make
   * a board look emptier than it is.
   *
   * Company-level ids, never a project's local name, so a cross-project view
   * filters correctly across two projects that renamed the same type differently.
   *
   * Optional so prefs saved before this filter existed still parse.
   */
  typeIds?: string[];
}

// Canonical MCP scope taxonomy (one source of truth across api/mcp/web).
export * from './mcp-scopes.js';

/**
 * What a column id may look like on the wire (TOADO-290).
 *
 * Here rather than in either package because both ends apply it: the API
 * refuses anything else, and the extension refuses it before starting a
 * capture, so a value the server would reject costs a "pick again" rather
 * than a 400 paid at the end of the composer, the screenshot and the anchor.
 * Two copies of this rule in two packages is how the next surface gets it
 * wrong, and a comment claiming they are shared while they are copied is
 * worse than either.
 */
export const MAX_COLUMN_ID_LENGTH = 64;
export const COLUMN_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * Strings in the order the server's `COLLATE "C"` puts them, so a column tie
 * is broken the same way wherever it is broken: the API's `boardColumnOrder`
 * in SQL, and this in the API's own sorts, the extension and the mock backend.
 *
 * That collation compares UTF-8 bytes, and UTF-8 keeps code point order, so
 * this compares code points. JavaScript's own `<` compares UTF-16 code units,
 * which agrees for every character below U+D800 and disagrees above it: a
 * character past U+FFFF is two surrogates starting at U+D800, which sort before
 * U+E000 though the character itself comes after. A column id is whatever its
 * creator sent (the replace-columns route keeps a caller's id), so it can hold
 * either.
 */
export function compareByCodePoint(a: string, b: string): number {
  const left = a[Symbol.iterator]();
  const right = b[Symbol.iterator]();
  for (;;) {
    const x = left.next();
    const y = right.next();
    if (x.done || y.done) return x.done === y.done ? 0 : x.done ? -1 : 1;
    const difference = x.value.codePointAt(0)! - y.value.codePointAt(0)!;
    if (difference !== 0) return difference;
  }
}

/** Is this a column id both ends will accept? */
export function isColumnId(value: unknown): value is string {
  return typeof value === 'string'
    && value.length > 0
    && value.length <= MAX_COLUMN_ID_LENGTH
    && COLUMN_ID_PATTERN.test(value);
}
export * from './placedSize';
export * from './widgetRecordingSize';
export * from './userAgent';

export type { SelfServicePlan, BillingInterval, BillingPlanDefinition, BillingFeatureRow, BillingPlansResponse, EnterpriseInquiryInput } from './billingPresentation';
export * from './sessionTrail';
export * from './sessionTimeline';
export * from './reproduction';
export * from './clickMarks';
export * from './trailRoleWords';
export * from './tours';
export * from './agentCheck';
export * from './agentInstructions';
